/**
 * v18: read aloud. The youngest pupils cannot read the questions yet, so a question can be read out by the browser's own voice
 * (the Web Speech API – nothing is sent to our server or anyone else, and nothing is stored).
 *
 *  - "auto"  : every new question is read out (default for 0.–2. klasse)
 *  - "tap"   : a loudspeaker button on the question reads it when pressed (default for everyone else)
 *  - "off"   : no button, no voice
 * Whether there is a good Danish voice depends on the device; if the browser has none we still try, and if it cannot speak at all the button is hidden.
 */
export type ReadMode = "auto" | "tap" | "off";
const KEY = "edu-read";

export function canSpeak(): boolean {
  try {
    return typeof window !== "undefined" && "speechSynthesis" in window && typeof SpeechSynthesisUtterance !== "undefined";
  } catch {
    return false;
  }
}

export function defaultReadMode(grade: number | null | undefined): ReadMode {
  return grade != null && grade <= 2 ? "auto" : "tap";
}
export function readMode(grade: number | null | undefined): ReadMode {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "auto" || v === "tap" || v === "off") return v;
  } catch { /* storage can be blocked */ }
  return defaultReadMode(grade);
}
export function setReadMode(m: ReadMode): void {
  try { localStorage.setItem(KEY, m); } catch { /* ignore */ }
  if (m === "off") stopSpeaking();
}

/** Maths symbols are read the Danish way ("3 × 4" -> "3 gange 4"). */
export function spoken(text: string): string {
  return text
    .replace(/×|\*/g, " gange ")
    .replace(/÷/g, " divideret med ")
    .replace(/(\d)\s*:\s*(?=\d)/g, "$1 divideret med ")
    .replace(/[−–]/g, " minus ")
    .replace(/\+/g, " plus ")
    .replace(/=/g, " er lig med ")
    .replace(/%/g, " procent")
    .replace(/_{2,}/g, " … ")
    .replace(/\s+/g, " ")
    .trim();
}

/** the whole question as one text: the question, then "A: …, B: …" */
export function questionToSpeech(q: { text: string; kind: string; options: string[] }): string {
  const head = spoken(q.text);
  if (q.kind === "short" || q.options.length === 0) return head;
  if (q.kind === "tf") return head;
  return head + ". " + q.options.map((o, i) => `${"ABCDEF"[i]}: ${spoken(o)}`).join(". ") + ".";
}

function pickVoice(): SpeechSynthesisVoice | null {
  try {
    const all = window.speechSynthesis.getVoices();
    return all.find((v) => v.lang === "da-DK") ?? all.find((v) => v.lang?.toLowerCase().startsWith("da")) ?? null;
  } catch {
    return null;
  }
}

let current: SpeechSynthesisUtterance | null = null;

export function speak(text: string, onState?: (speaking: boolean) => void): void {
  if (!canSpeak() || !text.trim()) return;
  stopSpeaking();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "da-DK";
  const v = pickVoice();
  if (v) u.voice = v;
  u.rate = 0.9; // a little slower for small ears
  u.pitch = 1.05;
  u.onstart = () => onState?.(true);
  u.onend = () => { if (current === u) current = null; onState?.(false); };
  u.onerror = () => { if (current === u) current = null; onState?.(false); };
  current = u;
  try { window.speechSynthesis.speak(u); } catch { onState?.(false); }
}

export function stopSpeaking(): void {
  current = null;
  try { if (canSpeak()) window.speechSynthesis.cancel(); } catch { /* ignore */ }
}
